import { useState, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../../../store/appStore';
import { Contact } from '../../../config/api';

// Types for MCP workflow integration
interface ClientOnboardingWorkflowPayload {
  clientId: string;
  realmId: string;
  assignedUserId: string;
  matterId?: string;
  onboardingPriority: 'standard' | 'high' | 'vip';
  communicationPreferences: {
    preferredChannel: 'email' | 'phone' | 'portal';
    frequency: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  };
}

interface ClientRetentionWorkflowPayload {
  clientId: string;
  realmId: string;
  triggerEvent: 'periodic_review' | 'satisfaction_decline' | 'payment_delay' | 'communication_gap' | 'matter_completion' | 'complaint_received';
  reviewPeriod: '90d' | '6m' | '1y';
  riskThreshold?: number;
}

interface MCPWorkflowResponse {
  workflowId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  result?: any;
  error?: string;
}

interface ContactWithAI extends Contact {
  // AI-enhanced fields from our backend
  ai_insights?: {
    relationship_score: number;
    retention_risk: 'low' | 'medium' | 'high';
    satisfaction_prediction: number;
    recommended_actions: string[];
    last_analysis_date: string;
  };
  onboarding_status?: {
    completed: boolean;
    completion_percentage: number;
    next_steps: string[];
  };
}

type BackendError = Error & { backendBody?: any };

export const useContacts = () => {
  const { keycloak } = useKeycloak();
  const { 
    contacts, 
    setContacts, 
    setContactsLoading, 
    setContactsError 
  } = useAppStore();
  
  const [workflowStatus, setWorkflowStatus] = useState<Record<string, string>>({});

  // Base API URL - use relative URLs for NGINX proxy
  const API_BASE = '';

  // Get JWT token from Keycloak
  const getAuthToken = () => {
    console.log('🔑 [Token Debug] Getting token from React Keycloak provider...');
    
    // 1. First try the React Keycloak provider (most reliable)
    if (keycloak?.token) {
      console.log('✅ [Token Debug] Found token via React Keycloak provider');
      return keycloak.token;
    }
    
    // 2. Check if the global window object has the parsed token info
    const globalToken = (window as any).webelieveTokenParsed;
    if (globalToken) {
      console.log('🔍 [Token Debug] Found parsed token in window.webelieveTokenParsed:', globalToken);
      // If we have parsed token info, we need to reconstruct the JWT or find it elsewhere
    }
    
    // 3. Fall back to storage-based detection
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
    
    console.log('🔍 [Token Debug] Falling back to storage detection...');
    for (const storage of [sessionStorage, localStorage]) {
      for (const key of Object.keys(storage)) {
        const value = storage.getItem(key);
        if (!value) continue;
        
        if (jwtPattern.test(value)) {
          console.log(`✅ [Token Debug] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'}: ${key}`);
          return value;
        }
        
        if (value.startsWith('{')) {
          try {
            const parsed = JSON.parse(value);
            const tokenFields = ['token', 'access_token', 'accessToken', 'idToken', 'id_token'];
            for (const field of tokenFields) {
              if (parsed[field] && jwtPattern.test(parsed[field])) {
                console.log(`✅ [Token Debug] Found JWT token in ${storage === localStorage ? 'localStorage' : 'sessionStorage'} ${key}.${field}`);
                return parsed[field];
              }
            }
          } catch (e) { /* ignore */ }
        }
      }
    }
    
    console.log('❌ [Token Debug] No JWT token found');
    console.log('🔍 [Token Debug] Keycloak authenticated:', keycloak?.authenticated);
    console.log('🔍 [Token Debug] Keycloak instance:', !!keycloak);
    
    return '';
  };

  // Create headers with JWT authentication
  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getAuthToken()}`
  });

  // Fetch all contacts with filtering and search
  const fetchContacts = useCallback(async (filters?: {
    status?: string;
    contact_type?: string;
    tags?: string;
    search?: string;
  }) => {
    try {
      console.log('🔍 [useContacts] fetchContacts called with filters:', filters);
      
      setContactsLoading(true);
      setContactsError(null);

      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.contact_type) params.append('contact_type', filters.contact_type);
      if (filters?.tags) params.append('tags', filters.tags);
      if (filters?.search) params.append('search', filters.search);

      const url = `${API_BASE}/api/v1/clients?${params.toString()}`;
      const headers = getAuthHeaders();
      
      console.log('🔍 [useContacts] Making API request:', { 
        url, 
        headers: { ...headers, Authorization: headers.Authorization ? 'Bearer [REDACTED]' : 'Missing' } 
      });

      const response = await fetch(url, { headers });

      console.log('📡 [useContacts] API Response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [useContacts] API Error Response:', errorText);
        throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ [useContacts] API Success - Response data:', data);
      console.log('✅ [useContacts] Contacts array length:', Array.isArray(data) ? data.length : (data.clients ? data.clients.length : 'Not an array'));
      
      const contactsArray = data.clients || data;
      setContacts(contactsArray);
      console.log('✅ [useContacts] Contacts set in store. Total contacts:', contactsArray.length);
      
      return contactsArray;
    } catch (error) {
      console.error('💥 [useContacts] Failed to fetch contacts:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch contacts';
      setContactsError(errorMessage);
      console.error('💥 [useContacts] Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: errorMessage,
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });
      throw error;
    } finally {
      console.log('🔄 [useContacts] Setting loading to false');
      setContactsLoading(false);
    }
  }, [setContacts, setContactsLoading, setContactsError, API_BASE]);

  // Fetch single contact by ID with AI insights
  const fetchContactById = useCallback(async (contactId: string): Promise<ContactWithAI | null> => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/clients/${contactId}`,
        {
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to fetch contact: ${response.statusText}`);
      }

      const contact = await response.json();
      return contact;
    } catch (error) {
      console.error('Failed to fetch contact:', error);
      setContactsError(error instanceof Error ? error.message : 'Failed to fetch contact');
      throw error;
    }
  }, [API_BASE, setContactsError]);

  // Create new contact
  const createContact = useCallback(async (contactData: Omit<Contact, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setContactsLoading(true);
      
      console.log('🔄 [useContacts] createContact called with data:', contactData);
      
      // Validate required fields
      if (!contactData.name || contactData.name.trim() === '') {
        throw new Error('Contact name is required');
      }
      
      // Check for duplicate name in current contacts
      const existingByName = contacts.find(
        contact => contact.name?.toLowerCase() === contactData.name.toLowerCase()
      );
      if (existingByName) {
        throw new Error(`Contact with name "${contactData.name}" already exists`);
      }
      
      // Check for duplicate email if provided
      if (contactData.email) {
        const existingByEmail = contacts.find(
          contact => contact.email?.toLowerCase() === contactData.email?.toLowerCase()
        );
        if (existingByEmail) {
          throw new Error(`Contact with email "${contactData.email}" already exists`);
        }
      }
      
      // Format data for backend API (CreateClientDto format)
      const backendData = {
        client_name: contactData.name.trim(),
        contact_info: {
          email: contactData.email?.trim() || undefined,
          phone: contactData.phone?.trim() || undefined,
          address: contactData.address?.trim() || undefined
        },
        client_language_preference: 'en' // Default to English
      };
      
      console.log('🔄 [useContacts] Formatted data for backend:', backendData);
      
      const response = await fetch(
        `${API_BASE}/api/v1/clients`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(backendData)
        }
      );

      console.log('📡 [useContacts] Create API Response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        let errorBody: any = errorText;
        try {
          errorBody = JSON.parse(errorText);
        } catch (e) { /* not JSON, keep as text */ }

        if (response.status === 409) {
          let conflictMessage = 'Contact already exists';
          if (typeof errorBody === 'object' && errorBody !== null && ('error' in errorBody || 'message' in errorBody)) {
            conflictMessage = (errorBody.error as string) || (errorBody.message as string) || conflictMessage;
          } else if (typeof errorBody === 'string') {
            conflictMessage = errorBody;
          }
          const err = new Error(conflictMessage) as BackendError;
          err.name = 'ConflictError';
          err.backendBody = errorBody;
          throw err;
        } else if (response.status === 400) {
          let validationMessage = 'Invalid contact data';
          if (typeof errorBody === 'object' && errorBody !== null && ('error' in errorBody || 'message' in errorBody)) {
            validationMessage = (errorBody.error as string) || (errorBody.message as string) || validationMessage;
          } else if (typeof errorBody === 'string') {
            validationMessage = errorBody;
          }
          const err = new Error(validationMessage) as BackendError;
          err.name = 'ValidationError';
          err.backendBody = errorBody;
          throw err;
        } else {
          const err = new Error(`Failed to create contact: ${response.status} ${response.statusText}`) as BackendError;
          err.name = 'ApiError';
          err.backendBody = errorBody;
          throw err;
        }
      }

      const newContact = await response.json();
      console.log('✅ [useContacts] Contact created successfully:', newContact);
      
      // Update local state
      const updatedContacts = [...contacts, newContact];
      setContacts(updatedContacts);
      
      return newContact;
    } catch (error) {
      console.error('💥 [useContacts] Failed to create contact:', error);
      setContactsError(error instanceof Error ? error.message : 'Failed to create contact');
      throw error;
    } finally {
      setContactsLoading(false);
    }
  }, [contacts, setContacts, setContactsLoading, setContactsError, API_BASE]);

  // Update existing contact
  const updateContact = useCallback(async (contactId: string, updates: Partial<Contact>) => {
    try {
      setContactsLoading(true);

      // Transform frontend Contact format to backend UpdateClientDto format
      const backendUpdateData: any = {};
      
      // Map name to client_name
      if (updates.name !== undefined) {
        backendUpdateData.client_name = updates.name.trim();
      }
      
      // Map contact info fields to contact_info object
      const contactInfo: any = {};
      if (updates.email !== undefined) {
        contactInfo.email = updates.email?.trim() || undefined;
      }
      if (updates.phone !== undefined) {
        contactInfo.phone = updates.phone?.trim() || undefined;
      }
      if (updates.address !== undefined) {
        contactInfo.address = updates.address?.trim() || undefined;
      }
      
      // Only include contact_info if we have contact info updates
      if (Object.keys(contactInfo).length > 0) {
        backendUpdateData.contact_info = contactInfo;
      }
      
      // Map status with reverse mapping (frontend 'archived' → backend 'inactive')
      if (updates.status !== undefined) {
        const statusMap: Record<string, string> = {
          lead: 'lead',
          qualified: 'qualified',
          converted: 'converted',
          archived: 'inactive',
        };
        backendUpdateData.status = statusMap[updates.status] || updates.status;
      }

      // Map tags directly
      if (updates.tags !== undefined) {
        backendUpdateData.tags = updates.tags;
      }

      // Map frontend 'notes' to backend 'intake_notes'
      if (updates.notes !== undefined) {
        backendUpdateData.intake_notes = updates.notes;
      }

      // Map frontend 'custom_fields' to backend 'intake_form_responses'
      if (updates.custom_fields !== undefined) {
        backendUpdateData.intake_form_responses = updates.custom_fields;
      }

      console.log('🔄 [useContacts] Transformed update data for backend:', backendUpdateData);

      const response = await fetch(
        `${API_BASE}/api/v1/clients/${contactId}`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify(backendUpdateData)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update contact: ${response.statusText}`);
      }

      const updatedContact = await response.json();
      
      // Update local state
      const updatedContacts = contacts.map(contact =>
        contact.id === contactId ? { ...contact, ...updatedContact } : contact
      );
      setContacts(updatedContacts);
      
      return updatedContact;
    } catch (error) {
      console.error('Failed to update contact:', error);
      setContactsError(error instanceof Error ? error.message : 'Failed to update contact');
      throw error;
    } finally {
      setContactsLoading(false);
    }
  }, [contacts, setContacts, setContactsLoading, setContactsError, API_BASE]);

  // Delete contact (soft delete)
  const deleteContact = useCallback(async (contactId: string) => {
    try {
      setContactsLoading(true);

      const response = await fetch(
        `${API_BASE}/api/v1/clients/${contactId}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        // Capture detailed error response from backend
        let errorMessage = `Failed to delete contact: ${response.statusText}`;
        
        try {
          const errorResponseText = await response.text();
          console.log('🔍 [deleteContact] Backend error response:', errorResponseText);
          
          if (errorResponseText) {
            // Try to parse JSON error response
            try {
              const errorData = JSON.parse(errorResponseText);
              errorMessage = errorData.error || errorData.message || errorData.description || errorMessage;
            } catch (jsonError) {
              // If not JSON, use the text response directly
              errorMessage = errorResponseText;
            }
          }
        } catch (textError) {
          console.log('⚠️ [deleteContact] Could not read error response text:', textError);
        }
        
        console.log('🔍 [deleteContact] Final error message:', errorMessage);
        throw new Error(errorMessage);
      }

      // Remove from local state
      const updatedContacts = contacts.filter(contact => contact.id !== contactId);
      setContacts(updatedContacts);
      
      return true;
    } catch (error) {
      console.error('Failed to delete contact:', error);
      // DON'T set global error state for deletion failures - let the modal handle it
      // setContactsError(error instanceof Error ? error.message : 'Failed to delete contact');
      throw error;
    } finally {
      setContactsLoading(false);
    }
  }, [contacts, setContacts, setContactsLoading, API_BASE]);

  // Execute Client Onboarding Workflow
  const executeOnboardingWorkflow = useCallback(async (payload: ClientOnboardingWorkflowPayload): Promise<MCPWorkflowResponse> => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/mcp/clients/onboarding`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to execute onboarding workflow: ${response.statusText}`);
      }

      const result = await response.json();
      setWorkflowStatus(prev => ({ ...prev, [payload.clientId]: 'running' }));
      
      return result;
    } catch (error) {
      console.error('Failed to execute onboarding workflow:', error);
      throw error;
    }
  }, [API_BASE]);

  // Execute Client Retention Analysis Workflow
  const executeRetentionWorkflow = useCallback(async (payload: ClientRetentionWorkflowPayload): Promise<MCPWorkflowResponse> => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/mcp/clients/retention`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to execute retention workflow: ${response.statusText}`);
      }

      const result = await response.json();
      setWorkflowStatus(prev => ({ ...prev, [payload.clientId]: 'running' }));
      
      return result;
    } catch (error) {
      console.error('Failed to execute retention workflow:', error);
      throw error;
    }
  }, [API_BASE]);

  // Get workflow status
  const getWorkflowStatus = useCallback(async (workflowId: string) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/mcp/clients/status/${workflowId}`,
        {
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch workflow status: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch workflow status:', error);
      throw error;
    }
  }, [API_BASE]);

  // Get client analytics/metrics
  const getClientMetrics = useCallback(async (clientId: string) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/clients/${clientId}/metrics`,
        {
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch client metrics: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch client metrics:', error);
      throw error;
    }
  }, [API_BASE]);

  // Get client retention analysis
  const getRetentionAnalysis = useCallback(async (clientId: string) => {
    try {
      const response = await fetch(
        `${API_BASE}/api/v1/clients/${clientId}/retention`,
        {
          headers: getAuthHeaders()
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch retention analysis: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch retention analysis:', error);
      throw error;
    }
  }, [API_BASE]);

  return {
    // Data
    contacts,
    workflowStatus,
    
    // Basic CRUD operations
    fetchContacts,
    fetchContactById,
    createContact,
    updateContact,
    deleteContact,
    
    // MCP Workflow operations
    executeOnboardingWorkflow,
    executeRetentionWorkflow,
    getWorkflowStatus,
    
    // Analytics
    getClientMetrics,
    getRetentionAnalysis,
    
    // Utility
    loading: useAppStore(state => state.contactsLoading),
    error: useAppStore(state => state.contactsError)
  };
}; 